import { useEffect, useState } from "react";
import { Button } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { TableState, TableStateFunctions } from "@/types/tableState.types";
import { useWorkspaceActions } from "@/hooks/useWorkspaceActions";
import { useWorkspaceGuardActions } from "@/contexts/WorkspaceNavigationGuardContext";
import { useTemplateDrift } from "@/hooks/useTemplateDrift";
import { useSuperuserMode } from "@/contexts/useSuperuser";
import { Workspace, WorkspaceList, WorkspaceMutations } from "./workspace";
import { WorkspaceDrawer } from "./WorkspaceDrawer";
import { WorkspaceModal } from "./SaveWorkspaceButton/WorkspaceModal";
import { UnsavedChangesModal } from "./UnsavedChangesModal";
import {
  getWorkspaceBannerCopy,
  getWorkspaceModalCopy,
  shouldShowWorkspaceBanner,
} from "./workspaceFlow";

type WorkspaceSectionProps = {
  tableName: string;
  workspaces: WorkspaceList;
  currentWorkspace: Workspace | null;
  setCurrentWorkspace: (workspaceId: number) => void;
  mutations: WorkspaceMutations;
  initialTableState: TableState;
  currentTableState: TableState;
  tableStateFunctions: TableStateFunctions;
  isDefault: boolean;
  isDrawerOpen: boolean;
  setIsDrawerOpen: (open: boolean) => void;
  metadata?: {
    columns?: { field: string; label: string }[];
    filters?: Record<string, unknown>;
  };
};

export function WorkspaceSection({
  tableName,
  workspaces,
  currentWorkspace,
  setCurrentWorkspace,
  mutations,
  initialTableState,
  currentTableState,
  tableStateFunctions,
  isDefault,
  isDrawerOpen,
  setIsDrawerOpen,
  metadata,
}: WorkspaceSectionProps) {
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [isWorkspaceSwitching, setIsWorkspaceSwitching] = useState(false);
  const [showSaveAsModal, setShowSaveAsModal] = useState(false);

  const { isSuperuserMode } = useSuperuserMode();

  const isVivoTemplate = !!currentWorkspace?.is_vivo_template;

  const hasDrift = useTemplateDrift(currentTableState, currentWorkspace);
  const bannerCopy = getWorkspaceBannerCopy({
    isDefault,
    isVivoTemplate,
    isSuperuserMode,
    hasDrift,
  });
  const modalCopy = getWorkspaceModalCopy({
    isDefault,
    isVivoTemplate,
    isSuperuserMode,
    hasDrift,
  });
  const shouldRenderBanner = shouldShowWorkspaceBanner({
    isDefault,
    hasDrift,
    isSwitchingWorkspace: isWorkspaceSwitching,
    currentWorkspaceId: currentWorkspace?.id,
    stateWorkspaceId: currentTableState.workspaceId,
  });

  useEffect(() => {
    setBannerDismissed(false);
  }, [currentWorkspace?.id, currentWorkspace?.modified_at]);

  useEffect(() => {
    if (!currentWorkspace) return;
    const needsStamp =
      !currentTableState.workspaceId ||
      currentTableState.workspaceId !== currentWorkspace.id;
    if (needsStamp) {
      tableStateFunctions.setWorkspaceMeta(
        currentWorkspace.id,
        currentWorkspace.modified_at,
      );
      setIsWorkspaceSwitching(false);
    }
  }, [
    currentWorkspace,
    currentTableState.workspaceId,
    currentWorkspace?.modified_at,
    tableStateFunctions,
  ]);

  const actions = useWorkspaceActions({
    tableName,
    workspaces,
    currentWorkspace,
    setCurrentWorkspace,
    mutations,
    initialTableState,
    tableStateFunctions,
    metadata,
    onDrawerClose: () => setIsDrawerOpen(false),
  });

  const {
    isModalOpen,
    isSaving,
    runGuardedAction,
    handleDiscard,
    handleCancel,
  } = useWorkspaceGuardActions(Boolean(modalCopy) && !isDefault);

  const handleApplyLatest = () => {
    if (!currentWorkspace) return;
    tableStateFunctions.clearLocalStorage();
    tableStateFunctions.reset(initialTableState);
    tableStateFunctions.setWorkspaceMeta(
      currentWorkspace.id,
      currentWorkspace.modified_at,
    );
    setBannerDismissed(true);
  };

  const handleSelectWorkspace = (workspaceId: number) => {
    setIsWorkspaceSwitching(true);
    actions.handleSelectWorkspace(workspaceId);
  };

  const handleModalDiscard = () => {
    tableStateFunctions.clearLocalStorage();
    handleDiscard();
  };

  const shouldShowBanner =
    Boolean(bannerCopy) && !bannerDismissed && shouldRenderBanner;

  let modalWorkspaceAction: (() => void) | undefined;
  if (modalCopy?.workspaceActionType === "applyLatest") {
    modalWorkspaceAction = () => {
      void runGuardedAction(async () => {
        handleApplyLatest();
      });
    };
  } else if (modalCopy?.workspaceActionType === "saveCurrent") {
    modalWorkspaceAction = () => {
      void runGuardedAction(() =>
        actions.handleSaveWorkspace(currentTableState),
      );
    };
  }

  return (
    <>
      {shouldShowBanner && bannerCopy && (
        <div
          data-testid={bannerCopy.testId}
          className="mb-2 w-full rounded-sm border border-[#BCA048] bg-[#FEF7DF] p-3"
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="m-0 min-w-0 flex-1 text-sm text-[#283833]">
              {bannerCopy.text}
            </p>
            <div className="flex flex-wrap items-center justify-end gap-2">
              {bannerCopy.actions.map((action) => {
                const isPrimary =
                  action.key === "saveCurrent" || action.key === "applyLatest";
                const handleAction = () => {
                  if (action.key === "reset") {
                    tableStateFunctions.reset(initialTableState);
                  } else if (action.key === "saveAsNew") {
                    setShowSaveAsModal(true);
                  } else if (action.key === "saveCurrent") {
                    void runGuardedAction(() =>
                      actions.handleSaveWorkspace(currentTableState),
                    );
                  } else if (action.key === "applyLatest") {
                    void runGuardedAction(async () => {
                      handleApplyLatest();
                    });
                  }
                };

                const isReset = action.key === "reset";
                let buttonClassName =
                  "px-2 py-1 h-auto bg-white text-[#064C68] border-[#D9D9D9] hover:border-[#064C68]!";
                if (isPrimary)
                  buttonClassName =
                    "px-2 py-1 h-auto bg-[#064C68] text-white hover:bg-[#04384d]! hover:text-white! border-[#064C68] hover:border-[#04384d]!";
                if (isReset)
                  buttonClassName = "px-2 py-1 h-auto text-[#064C68]";
                return (
                  <Button
                    key={action.key}
                    onClick={handleAction}
                    type={isReset ? "text" : "default"}
                    icon={isReset ? <ReloadOutlined /> : undefined}
                    className={buttonClassName}
                  >
                    {action.label}
                  </Button>
                );
              })}
            </div>
          </div>
        </div>
      )}
      {showSaveAsModal && (
        <WorkspaceModal
          isOpen
          existingNames={workspaces.map((w) => w.title)}
          onConfirm={async (name, isVivoTemplate) => {
            await actions.handleSaveAsNewWorkspace(
              name,
              currentTableState,
              isVivoTemplate,
            );
            setShowSaveAsModal(false);
          }}
          onCancel={() => setShowSaveAsModal(false)}
        />
      )}
      <WorkspaceDrawer
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        workspaces={workspaces}
        selectedWorkspaceId={currentWorkspace?.id ?? 0}
        onSelectWorkspace={handleSelectWorkspace}
        onDuplicate={actions.handleDuplicate}
        onRename={actions.handleRename}
        onDelete={actions.handleDelete}
        onCreateNew={actions.handleCreateNew}
        onSaveAsNewWorkspace={(name) =>
          actions.handleSaveAsNewWorkspace(name, currentTableState)
        }
        onSaveCurrentWorkspace={() =>
          actions.handleSaveWorkspace(currentTableState)
        }
        onApplyLatestTemplate={handleApplyLatest}
        currentTableState={currentTableState}
        hasUnsavedChanges={!isDefault}
        hasDrift={hasDrift}
      />
      {modalCopy && (
        <UnsavedChangesModal
          isOpen={isModalOpen}
          isSaving={isSaving}
          title={modalCopy.title}
          description={modalCopy.description}
          workspaceName={currentWorkspace?.title ?? ""}
          discardLabel={modalCopy.discardLabel}
          saveAsNewLabel={modalCopy.saveAsNewLabel}
          saveAsNewConfirmLabel={modalCopy.saveAsNewConfirmLabel}
          saveCurrentWorkspaceLabel={modalCopy.saveCurrentWorkspaceLabel}
          saveCurrentWorkspaceConfirmLabel={
            modalCopy.saveCurrentWorkspaceConfirmLabel
          }
          saveCurrentWorkspaceWarning={modalCopy.saveCurrentWorkspaceWarning}
          applyLatestTemplateLabel={modalCopy.applyLatestTemplateLabel}
          onCancel={handleCancel}
          onDiscard={handleModalDiscard}
          onSaveAsNew={(copyName) =>
            runGuardedAction(() =>
              actions.handleSaveAsNewWorkspace(copyName, currentTableState),
            )
          }
          onWorkspaceAction={modalWorkspaceAction}
        />
      )}
      <span
        data-testid="workspace-applied-state"
        data-workspace-id={currentWorkspace?.id ?? ""}
        data-workspace-title={currentWorkspace?.title ?? ""}
        data-workspace-default={isDefault ? "true" : "false"}
        className="hidden"
      />
    </>
  );
}
